import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";
import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { Icon } from "@/components/Icon/Icon";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { classNames } from "@/utils/classNames";
import { User } from "@/pages/Public/pages/Login/schema/login";
import { getLinkedinTier, linkedinTierLabel } from "@/utils/getLinkedinTier";

import { SenderNote, SenderOption } from "./senderOptions";

interface SenderSelectProps {
	/**
	 * Every colleague the picker lists: the enabled ones can be chosen, the others show why not
	 * (see `buildSenderOptions`).
	 */
	options: SenderOption[];
	selectedSendingUser: User | null;
	setSelectedSendingUser: (user: User | null) => void;
	connectedUserIds?: Set<number>;
	/** Senders whose own invitation to the lead is pending: they say so, not "Not connected" (PREC-4216). */
	pendingInvitationUserIds?: Set<number>;
	connectionCheckDone?: boolean;
	prospectFirstName?: string | null;
	/**
	 * When true, each sender additionally shows its LinkedIn tier (Free / Premium /
	 * Sales Navigator). Free senders show an amber warning. Intended for the
	 * LinkedIn invite-note context only.
	 */
	showLinkedinTier?: boolean;
	/**
	 * Compact (DetailView "Chat" tab / extension panel): the trigger collapses to
	 * an avatar + chevron with a hover tooltip instead of the full name pill.
	 */
	compact?: boolean;
	/** Portal target (extension passes its shadow root); defaults to `document.body`. */
	portalContainer?: HTMLElement | null;
}

const AVATAR_PALETTE = [
	"bg-blue-action",
	"bg-green-500",
	"bg-purple-800",
	"bg-yellow-500",
	"bg-rb-turquoise",
	"bg-tag-dark",
];

const getInitials = (user: AvatarPerson) =>
	`${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();

const getAvatarColor = (userId: number) => AVATAR_PALETTE[userId % AVATAR_PALETTE.length];

type AvatarPerson = Pick<User, "id" | "firstName" | "lastName">;

/** The line a row shows for each note: the reason it is disabled, or a warning. */
const NOTE_KEYS = {
	linkedinDisconnected: "messages.generateBar.senderSelect.notes.linkedinDisconnected",
	nothingConnected: "messages.generateBar.senderSelect.notes.nothingConnected",
	noEmailForLead: "messages.generateBar.senderSelect.notes.noEmailForLead",
	noLinkedinForLead: "messages.generateBar.senderSelect.notes.noLinkedinForLead",
	noChannelForLead: "messages.generateBar.senderSelect.notes.noChannelForLead",
} as const satisfies Record<SenderNote, string>;

const SenderAvatar: FunctionComponent<{
	user: AvatarPerson;
	sizeClass?: string;
	textClass?: string;
}> = ({ user, sizeClass = "w-8 h-8 text-xs", textClass = "text-white font-semibold" }) => (
	<div
		className={classNames(
			"flex items-center justify-center rounded-full shrink-0",
			sizeClass,
			textClass,
			getAvatarColor(user.id)
		)}
	>
		{getInitials(user) || "?"}
	</div>
);

const MENU_WIDTH = 280;
/** Ideal menu height (header + sender list) before it must scroll. */
const PREFERRED_MAX_HEIGHT = 360;
/** Gap kept between the menu and the viewport edge / the trigger. */
const MENU_MARGIN = 8;
/** Smallest height we let the menu shrink to before it just scrolls internally. */
const MIN_MENU_HEIGHT = 160;

export const SenderSelect: FunctionComponent<SenderSelectProps> = ({
	options,
	selectedSendingUser,
	setSelectedSendingUser,
	connectedUserIds,
	pendingInvitationUserIds,
	connectionCheckDone,
	prospectFirstName,
	showLinkedinTier,
	compact,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState<{
		top?: number;
		bottom?: number;
		left: number;
		maxHeight: number;
	} | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	useEffect(() => {
		if (!open) {
			return;
		}

		const updatePosition = () => {
			if (!buttonRef.current) {
				return;
			}

			const rect = buttonRef.current.getBoundingClientRect();
			const overflowsRight = rect.left + MENU_WIDTH > window.innerWidth - MENU_MARGIN;
			const left = overflowsRight ? rect.right - MENU_WIDTH : rect.left;

			// Flip above the trigger when there isn't room below and there's more room
			// above; cap the height to the available space so the list scrolls internally
			// instead of spilling off the viewport edge.
			const spaceBelow = window.innerHeight - rect.bottom - MENU_MARGIN;
			const spaceAbove = rect.top - MENU_MARGIN;
			const openUp = spaceBelow < PREFERRED_MAX_HEIGHT && spaceAbove > spaceBelow;
			const available = Math.max(MIN_MENU_HEIGHT, openUp ? spaceAbove : spaceBelow);
			const maxHeight = Math.min(PREFERRED_MAX_HEIGHT, available);

			setPosition(
				openUp
					? { bottom: window.innerHeight - rect.top + 4, left, maxHeight }
					: { top: rect.bottom + 4, left, maxHeight }
			);
		};

		updatePosition();

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [open]);

	// The options arrive enabled first; within them, senders already connected to the lead lead.
	const sortedOptions = useMemo(() => {
		const connectedRank = (option: SenderOption) =>
			option.enabled && connectedUserIds?.has(option.id) ? 0 : 1;

		return options
			.map((option, index) => ({ option, index }))
			.sort(
				(a, b) =>
					Number(!a.option.enabled) - Number(!b.option.enabled) ||
					connectedRank(a.option) - connectedRank(b.option) ||
					a.index - b.index
			)
			.map(({ option }) => option);
	}, [options, connectedUserIds]);

	const getConnectionLine = (user: User): string | null => {
		if (!connectionCheckDone || !prospectFirstName) {
			return null;
		}

		if (!user.unipile?.isLoggedIn) {
			return null;
		}

		if (connectedUserIds?.has(user.id)) {
			return t("messages.generateBar.senderSelect.connectedTo", { name: prospectFirstName });
		}

		if (pendingInvitationUserIds?.has(user.id)) {
			return t("messages.generateBar.senderSelect.invitationPendingTo", {
				name: prospectFirstName,
			});
		}

		return t("messages.generateBar.senderSelect.notConnectedTo", { name: prospectFirstName });
	};

	// Always a dropdown once anyone is listed, even with a single usable sender, so the reason the
	// others cannot be chosen is always one click away (PREC-4244).
	if (options.length === 0) {
		const singleSenderTooltip = selectedSendingUser
			? showLinkedinTier
				? `${selectedSendingUser.firstName} ${selectedSendingUser.lastName} — ${linkedinTierLabel(
						getLinkedinTier(selectedSendingUser),
						t
					)}`
				: `${selectedSendingUser.firstName} ${selectedSendingUser.lastName}`
			: "No available sender";

		return (
			<Tooltip className="shrink-0" content={singleSenderTooltip}>
				{selectedSendingUser ? (
					<SenderAvatar user={selectedSendingUser} />
				) : (
					<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-action text-white text-xs font-bold cursor-default">
						?
					</div>
				)}
			</Tooltip>
		);
	}

	const selectedTitle = selectedSendingUser
		? `${selectedSendingUser.firstName} ${selectedSendingUser.lastName}`
		: t("basics.pleaseSelect");

	const handleSelect = (user: User) => {
		setSelectedSendingUser(user);
		setOpen(false);
	};

	const triggerButton = compact ? (
		<button
			ref={buttonRef}
			className={classNames(
				"input-styles w-max inline-flex items-center gap-1.5 pl-1.5 pr-2 hover:shadow-sm cursor-pointer",
				"!py-0 !h-[32px] !rounded-[10px]"
			)}
			data-testid="sender-select-trigger"
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				setOpen((prev) => !prev);
			}}
		>
			{selectedSendingUser ? (
				<SenderAvatar user={selectedSendingUser} sizeClass="w-6 h-6 text-[10px]" />
			) : (
				<div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-action text-white text-[10px] font-bold">
					?
				</div>
			)}
			<SvgIcon
				className="w-[16px] h-[16px] pointer-events-none text-text-meta"
				svgIcon={open ? ChevronUp : ChevronDown}
			/>
		</button>
	) : (
		<button
			ref={buttonRef}
			className="relative w-full"
			data-testid="sender-select-trigger"
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				setOpen((prev) => !prev);
			}}
		>
			<div
				className={classNames(
					"input-styles pr-12 w-max inline-flex pl-3 hover:shadow-sm",
					"py-1 !h-[32px] !rounded-[10px] text-xs font-semibold"
				)}
			>
				<div className="inline-flex flex-shrink-0 w-full text-left items-center">
					{selectedTitle}
				</div>
				<div className="absolute right-[10px] translate-y-[-50%] top-[50%]">
					<SvgIcon
						className="w-[20px] h-[20px] pointer-events-none"
						svgIcon={open ? ChevronUp : ChevronDown}
					/>
				</div>
			</div>
		</button>
	);

	return (
		<>
			{compact ? (
				<Tooltip
					className="shrink-0"
					content={t("messages.generateBar.senderSelect.tooltip", { name: selectedTitle })}
				>
					{triggerButton}
				</Tooltip>
			) : (
				triggerButton
			)}

			{open &&
				ReactDOM.createPortal(
					<>
						<div className="fixed inset-0 z-[9999]" onClick={() => setOpen(false)} />
						<div
							className="fixed flex flex-col bg-white border border-border-light rounded-xl shadow-lg z-[10000] overflow-hidden py-1"
							style={
								position
									? {
											top: position.top,
											bottom: position.bottom,
											left: position.left,
											width: MENU_WIDTH,
											maxHeight: position.maxHeight,
										}
									: { visibility: "hidden", width: MENU_WIDTH }
							}
							onClick={(e) => e.stopPropagation()}
						>
							<div className="shrink-0 px-3 pt-2 pb-1 text-[10px] font-semibold tracking-wider uppercase text-text-meta">
								{t("messages.generateBar.senderSelect.chooseSender")}
							</div>

							<div className="min-h-0 flex-1 overflow-y-auto">
								{sortedOptions.map((option) => {
									const { user } = option;
									const isSelected = selectedSendingUser?.id === option.id;
									const connectionLine = user && option.enabled ? getConnectionLine(user) : null;
									// A tier only means something for a sender whose LinkedIn can send.
									const tier =
										user && option.enabled && showLinkedinTier && user.unipile?.isLoggedIn
											? getLinkedinTier(user)
											: null;

									return (
										<button
											key={option.id}
											aria-disabled={!option.enabled}
											className={classNames(
												"flex flex-row items-center gap-2.5 w-full px-3 py-2 text-left transition-colors",
												!option.enabled
													? "bg-white cursor-not-allowed"
													: isSelected
														? "bg-blue-action-200 cursor-pointer"
														: "bg-white hover:bg-gray-100 cursor-pointer"
											)}
											data-testid={`sender-select-option-${option.id}`}
											disabled={!option.enabled}
											type="button"
											onClick={() => user && option.enabled && handleSelect(user)}
										>
											<div className={option.enabled ? undefined : "opacity-50"}>
												<SenderAvatar user={option} />
											</div>
											<div className="flex flex-col min-w-0 flex-1">
												<span
													className={classNames(
														"text-sm font-medium truncate",
														!option.enabled
															? "text-text-meta"
															: isSelected
																? "text-blue-action"
																: "text-text"
													)}
												>
													{option.firstName} {option.lastName}
												</span>
												{option.note && (
													<span
														className={classNames(
															"text-xs",
															option.enabled ? "text-text-warning" : "text-text-meta"
														)}
														data-testid={`sender-select-note-${option.id}`}
													>
														{t(NOTE_KEYS[option.note])}
													</span>
												)}
												{connectionLine && (
													<span className="text-xs text-text-meta truncate">{connectionLine}</span>
												)}
												{tier &&
													(tier === "free" ? (
														<Tooltip content={t("linkedin.tier.freeWarning")} direction="top">
															<span className="text-xs text-amber-600 font-medium truncate cursor-help">
																{linkedinTierLabel(tier, t)}
															</span>
														</Tooltip>
													) : (
														<span className="text-xs text-text-meta truncate">
															{linkedinTierLabel(tier, t)}
														</span>
													))}
											</div>
											{isSelected && (
												<Icon className="w-3 h-3 text-blue-action shrink-0" icon={faCheck} />
											)}
										</button>
									);
								})}
							</div>
						</div>
					</>,
					portalContainer ?? document.body
				)}
		</>
	);
};
